/**
 * bc-hero.js — Clair
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom element <bc-hero-section> — one instance per Hero section on the page.
 * Autoplay video is scoped to this element only; timers and observers are
 * cleaned up on disconnect (theme editor section unload/re-render).
 *
 * Attributes:
 *   data-video-delay — seconds before autoplay (default: 0)
 * ─────────────────────────────────────────────────────────────────────────────
 */

if (!customElements.get('bc-hero-section')) {
  class BcHeroSection extends HTMLElement {
    connectedCallback() {
      this._resetVideo();

      this._video = this.querySelector('.bc-hero__video');
      if (!this._video) return;

      this._delay = parseInt(this.dataset.videoDelay || '0', 10) * 1000;
      this._timer = null;

      this._muteBtn = this.querySelector('.bc-hero__mute');
      if (this._muteBtn) {
        this._onMuteToggle = () => this._toggleMute();
        this._muteBtn.addEventListener('click', this._onMuteToggle);
        this._syncMuteButton();
      }

      this._onEnded = () => {
        this._stopVideo();
      };

      this._video.addEventListener('ended', this._onEnded);

      this._io = new IntersectionObserver(
        (entries) => {
          const visible = entries[0]?.isIntersecting;
          if (visible) {
            this._schedulePlay();
          } else {
            this._cancelPlay();
            this._stopVideo();
          }
        },
        { threshold: 0.25 }
      );
      this._io.observe(this);
    }

    disconnectedCallback() {
      this._cancelPlay();
      this._io?.disconnect();
      if (this._video && this._onEnded) {
        this._video.removeEventListener('ended', this._onEnded);
      }
      if (this._muteBtn && this._onMuteToggle) {
        this._muteBtn.removeEventListener('click', this._onMuteToggle);
      }
    }

    _schedulePlay() {
      this._cancelPlay();
      this._timer = setTimeout(() => {
        if (!this._video) return;
        this._video.style.opacity = '1';
        this._showMuteButton(true);
        this._video.play().catch(() => {});
      }, this._delay);
    }

    /* Pause, rewind, hide and re-mute — shared by 'ended' and 'out of view'. */
    _stopVideo() {
      if (!this._video) return;
      this._video.pause();
      this._video.currentTime = 0;
      this._video.style.opacity = '0';
      this._video.muted = true;
      this._showMuteButton(false);
      this._syncMuteButton();
    }

    _toggleMute() {
      if (!this._video) return;
      this._video.muted = !this._video.muted;
      if (!this._video.muted) this._video.volume = 1;
      this._syncMuteButton();
    }

    _syncMuteButton() {
      if (!this._muteBtn) return;
      const muted = !this._video || this._video.muted;
      this._muteBtn.classList.toggle('is-muted', muted);
      this._muteBtn.setAttribute('aria-pressed', String(muted));
      this._muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    }

    _showMuteButton(show) {
      if (this._muteBtn) this._muteBtn.hidden = !show;
    }

    _cancelPlay() {
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
    }

    _resetVideo() {
      const video = this.querySelector('.bc-hero__video');
      if (!video) return;
      video.pause();
      video.currentTime = 0;
      video.style.opacity = '0';
    }
  }

  customElements.define('bc-hero-section', BcHeroSection);
}
